import React from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';

const ActualiteFormationGenrePage = () => {
  // Trois photos disponibles
  const photoDG = '/assets/genre/EAC_GENDER_OUVERTOURE.jpeg';
  const photoFamille = '/assets/genre/EAC_GENDER_PHOTO_FAMILLE.jpeg';
  const photoParticipants = '/assets/genre/EAC_GENDER_P.jpeg';

  // Photo de couverture (hero) : on utilise celle des participants (la plus représentative)
  const photoHero = photoParticipants;

  return (
    <div style={styles.container}>
      <NavigationBar />

      <main style={styles.main}>
        <header style={styles.header}>
          {/* BADGES */}
          <div style={styles.badges}>
            <span style={styles.badgeRed}>INSBU</span>
            <span style={styles.badgeGreen}>EAC</span>
            <span style={styles.badgeGray}>Actualité</span>
          </div>

          {/* TITRE ACCROCHEUR */}
          <h1 style={styles.title}>
            Le Burundi renforce ses capacités en statistiques de genre avec l’appui de la Communauté Est‑Africaine
          </h1>

          {/* MÉTADONNÉES */}
          <div style={styles.meta}>
            <span style={styles.metaItem}>
              <CalendarTodayIcon style={styles.metaIcon} /> 04 – 13 août 2026
            </span>
            <span style={styles.metaItem}>
              <LocationOnIcon style={styles.metaIcon} /> Royal Palace Hotel, Bujumbura
            </span>
            <span style={styles.metaItem}>
              <AccessTimeIcon style={styles.metaIcon} /> Lecture 4 min
            </span>
          </div>

          {/* PHOTO DE COUVERTURE */}
          <div style={styles.imageWrapper}>
            <img
              src={photoHero}
              alt="Ouverture de l'atelier sur les statistiques de genre"
              style={styles.heroImage}
              onError={(e) => {
                e.target.src = 'https://placehold.co/820x420/008D36/FFFFFF?text=Atelier+Genre+2026';
              }}
            />
            <p style={styles.imageCaption}>
              Vue de la salle lors de la cérémonie d’ouverture de l’atelier de formation, en présence des cadres ministériels et des experts de l’EAC.
            </p>
          </div>

          {/* CHAPEAU - RÉSUMÉ EN 5W */}
          <p style={styles.chapeau}>
            <strong>Bujumbura, 4 août 2026</strong> — L’<strong>Institut National de la Statistique du Burundi (INSBU)</strong>, avec le soutien technique et financier du <strong>Secrétariat Général de la Communauté Est‑Africaine (EAC)</strong>, a lancé ce mardi un atelier de formation stratégique de dix jours. Placé sous le thème de l’<strong>analyse, l’interprétation et la communication des données de genre</strong>, cet événement vise à outiller les cadres nationaux pour mieux intégrer la dimension genre dans les politiques publiques, en phase avec la Vision Burundi 2040-2060.
          </p>
        </header>

        {/* CORPS DE L'ARTICLE */}
        <article style={styles.article}>
          
          {/* SECTION 1 : CONTEXTE */}
          <h2 style={styles.h2}>
            <TrendingUpIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
            Un levier essentiel pour le développement inclusif
          </h2>
          <p style={styles.p}>
            Dans son discours d’ouverture, le Directeur Général de l’INSBU, <strong>M. Nicolas NDAYISHIMIYE</strong>, a rappelé que le Burundi, engagé dans la mise en œuvre de son Plan National de Développement et des Objectifs de Développement Durable, a besoin de <strong>statistiques fiables et désagrégées</strong> pour éclairer ses décisions.
          </p>
          <p style={styles.p}>
            « <em>Les statistiques de genre constituent un outil essentiel permettant de mesurer les inégalités entre les femmes et les hommes, de suivre les progrès accomplis et d'orienter les politiques vers un développement plus inclusif</em> », a-t-il souligné.
          </p>
          <p style={styles.p}>
            Cette formation est la <strong>troisième session</strong> du programme de renforcement des capacités initié par l’EAC. Elle succède à deux ateliers préalables tenus en 2025, qui avaient porté sur les concepts fondamentaux des statistiques de genre et la formation des formateurs nationaux.
          </p>

          {/* SECTION 2 : OBJECTIFS DE LA SESSION - AVEC BULLETS */}
          <h2 style={styles.h2}>
            <SchoolIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
            Au programme : de la théorie à la visualisation moderne
          </h2>
          <p style={styles.p}>
            Pendant dix jours, les participants issus des différents ministères sectoriels et de l’INSBU vont plancher sur des modules opérationnels afin de transformer les données brutes en véritables outils de plaidoyer. Les thématiques abordées incluent :
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}>
              <strong>Lignes directrices régionales</strong> : Harmonisation des standards de production des statistiques de genre au sein de la Communauté Est‑Africaine.
            </li>
            <li style={styles.li}>
              <strong>Méthodes d’analyse avancées</strong> : Maîtrise des techniques d’interprétation des données pour une lecture fine des disparités.
            </li>
            <li style={styles.li}>
              <strong>Communication et plaidoyer</strong> : Rédaction de rapports analytiques percutants et utilisation des <strong>outils modernes de visualisation</strong> (dataviz) pour vulgariser les résultats.
            </li>
          </ul>

          {/* ENCADRÉ CITATION DU DG */}
          <div style={styles.quoteBlock}>
            <ChatBubbleOutlineIcon style={{ fontSize: '2.5rem', color: '#008D36', marginBottom: '10px' }} />
            <blockquote style={styles.quoteText}>
              « Je suis convaincu que les compétences acquises contribueront, de manière significative, à améliorer non seulement la qualité des analyses produites, mais également l'utilisation des statistiques de genre dans la planification, le suivi et l'évaluation des politiques. »
            </blockquote>
            <p style={styles.quoteAuthor}>— Nicolas NDAYISHIMIYE, Directeur Général de l’INSBU</p>
          </div>

          {/* SECTION 3 : RÉSULTATS ATTENDUS */}
          <h2 style={styles.h2}>
            <CheckCircleIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
            Des retombées concrètes pour le Système Statistique National
          </h2>
          <p style={styles.p}>
            L’INSBU, en tant qu’organe technique de coordination, mise sur cette formation pour consolider la chaîne de production des données. Les résultats attendus à l’issue de cette session sont :
          </p>
          <div style={styles.gridOutcomes}>
            <div style={styles.outcomeCard}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '8px' }} />
              <span>Une meilleure qualité des rapports analytiques sectoriels.</span>
            </div>
            <div style={styles.outcomeCard}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '8px' }} />
              <span>Une intégration accrue du genre dans les politiques nationales.</span>
            </div>
            <div style={styles.outcomeCard}>
              <CheckCircleIcon style={{ color: '#008D36', marginRight: '8px' }} />
              <span>Une diffusion plus large et accessible des données via des outils visuels.</span>
            </div>
          </div>

          {/* GALERIE PHOTOS (3 images) */}
          <div style={styles.gallery}>
            <h2 style={styles.h2Gallery}>
              <PhotoCameraIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
              Galerie : Les temps forts de l’ouverture
            </h2>
            <div style={styles.galleryGrid}>
              <div style={styles.galleryItem}>
                <img
                  src={photoDG}
                  alt="DG Nicolas NDAYISHIMIYE"
                  style={styles.galleryImg}
                  onError={(e) => e.target.src = 'https://placehold.co/300x200/008D36/FFFFFF?text=DG+NDAYISHIMIYE'}
                />
                <p style={styles.galleryCaption}>Le DG de l’INSBU, M. Nicolas NDAYISHIMIYE, prononçant son allocution.</p>
              </div>
              <div style={styles.galleryItem}>
                <img
                  src={photoFamille}
                  alt="Photo de famille"
                  style={styles.galleryImg}
                  onError={(e) => e.target.src = 'https://placehold.co/300x200/008D36/FFFFFF?text=Photo+de+famille'}
                />
                <p style={styles.galleryCaption}>Photo de famille réunissant autorités, experts et participants.</p>
              </div>
              <div style={styles.galleryItem}>
                <img
                  src={photoParticipants}
                  alt="Participants en salle"
                  style={styles.galleryImg}
                  onError={(e) => e.target.src = 'https://placehold.co/300x200/008D36/FFFFFF?text=Participants'}
                />
                <p style={styles.galleryCaption}>Les participants en pleine écoute lors de la cérémonie d’ouverture.</p>
              </div>
            </div>
          </div>

          {/* CONCLUSION ET REMERCIEMENTS */}
          <div style={styles.conclusion}>
            <h2 style={styles.h2}>
              <PeopleIcon style={{ fontSize: '1.8rem', marginRight: '10px', verticalAlign: 'middle', color: '#008D36' }} />
              Vers une culture statistique renforcée
            </h2>
            <p style={styles.p}>
              En clôturant son discours, M. NDAYISHIMIYE a réitéré les remerciements du Système Statistique National à la <strong>Communauté Est-Africaine</strong> pour son accompagnement constant, ainsi qu’aux experts et participants pour leur engagement à promouvoir des statistiques de genre de qualité.
            </p>
            <p style={styles.p}>
              Cette initiative confirme la volonté du Burundi de disposer d’un système statistique robuste, au service d’une croissance inclusive et durable. La tenue prochaine de sessions similaires est déjà envisagée dans le cadre de la pérennisation des acquis.
            </p>
            <div style={styles.vive}>
              <p><strong>Vive la coopération statistique régionale !</strong></p>
              <p><strong>Vive les statistiques fiables au service du développement !</strong></p>
            </div>
          </div>

        </article>
      </main>

      <Footer />
    </div>
  );
};

// ================= STYLES =================
const styles = {
  container: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: 1,
    maxWidth: '820px',
    margin: '0 auto',
    padding: '40px 20px',
    width: '100%',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
    paddingBottom: '30px',
    borderBottom: '2px solid #e0e0e0',
  },
  badges: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  badgeRed: {
    backgroundColor: '#E30613',
    color: 'white',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  badgeGreen: {
    backgroundColor: '#008D36',
    color: 'white',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  badgeGray: {
    backgroundColor: '#6c757d',
    color: 'white',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  title: {
    color: '#1a1a1a',
    fontSize: '2.4rem',
    fontWeight: '700',
    lineHeight: 1.2,
    marginBottom: '12px',
    letterSpacing: '-0.3px',
  },
  meta: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '20px',
    marginBottom: '20px',
    fontSize: '0.9rem',
    color: '#6c757d',
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  },
  metaIcon: {
    fontSize: '1rem',
    color: '#008D36',
  },
  imageWrapper: {
    marginBottom: '24px',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  heroImage: {
    width: '100%',
    height: 'auto',
    maxHeight: '420px',
    objectFit: 'cover',
    display: 'block',
    borderRadius: '8px 8px 0 0',
  },
  imageCaption: {
    fontSize: '0.85rem',
    color: '#6c757d',
    textAlign: 'center',
    padding: '8px 0 4px 0',
    fontStyle: 'italic',
    backgroundColor: '#fafafa',
    margin: 0,
    borderBottom: '1px solid #eaeaea',
    borderRadius: '0 0 8px 8px',
  },
  chapeau: {
    fontSize: '1.2rem',
    lineHeight: 1.7,
    color: '#2c3e50',
    maxWidth: '700px',
    margin: '0 auto',
    fontWeight: '400',
    borderLeft: '4px solid #008D36',
    paddingLeft: '20px',
    textAlign: 'left',
  },
  article: {
    backgroundColor: 'white',
    padding: '40px 45px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
    marginBottom: '40px',
  },
  h2: {
    color: '#008D36',
    fontSize: '1.5rem',
    fontWeight: '600',
    marginTop: '40px',
    marginBottom: '16px',
    paddingBottom: '6px',
    borderBottom: '2px solid #eaeaea',
    display: 'flex',
    alignItems: 'center',
  },
  h2Gallery: {
    color: '#008D36',
    fontSize: '1.5rem',
    fontWeight: '600',
    marginTop: '20px',
    marginBottom: '16px',
    paddingBottom: '6px',
    borderBottom: '2px solid #eaeaea',
    display: 'flex',
    alignItems: 'center',
  },
  p: {
    fontSize: '1.05rem',
    lineHeight: 1.8,
    color: '#1e1e1e',
    marginBottom: '20px',
    textAlign: 'justify',
  },
  ul: {
    paddingLeft: '20px',
    marginBottom: '20px',
  },
  li: {
    fontSize: '1.05rem',
    lineHeight: 1.8,
    color: '#1e1e1e',
    marginBottom: '8px',
  },
  // BLOC CITATION
  quoteBlock: {
    backgroundColor: '#f0f7f2',
    padding: '25px 30px',
    borderRadius: '12px',
    margin: '30px 0',
    textAlign: 'center',
    border: '1px solid #d0e6d6',
  },
  quoteText: {
    fontSize: '1.4rem',
    lineHeight: 1.6,
    color: '#1a3b2e',
    fontWeight: '400',
    fontStyle: 'italic',
    margin: '0 0 10px 0',
  },
  quoteAuthor: {
    fontSize: '1rem',
    color: '#555',
    margin: 0,
    fontWeight: '600',
  },
  // GRILLE POUR LES RÉSULTATS
  gridOutcomes: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
  },
  outcomeCard: {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: '#f9fafb',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #eaeaea',
    fontSize: '0.95rem',
    lineHeight: 1.5,
    color: '#1e1e1e',
  },
  // GALERIE PHOTOS (3 items)
  gallery: {
    marginTop: '30px',
  },
  galleryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '15px',
  },
  galleryItem: {
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  galleryImg: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
    display: 'block',
  },
  galleryCaption: {
    fontSize: '0.8rem',
    color: '#555',
    padding: '6px 10px',
    textAlign: 'center',
    fontStyle: 'italic',
    margin: 0,
    borderTop: '1px solid #eaeaea',
    backgroundColor: '#ffffff',
  },
  conclusion: {
    marginTop: '40px',
    paddingTop: '20px',
    borderTop: '2px solid #eaeaea',
  },
  vive: {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '1.1rem',
    lineHeight: 2,
    color: '#008D36',
    fontWeight: '600',
  },
};

export default ActualiteFormationGenrePage;